import { useEffect } from 'react';
import useAssetCancellationRequestCreate from 'modules/queries/asset/cancellation/request/useAssetCancellationRequestCreate';
import useSupportRequestDetails from 'modules/queries/support/request/useDetails';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import CircleCheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleCheck';
import Button from '@liquidweb-dev/undercurrent/Button';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import { pseudoClasses } from '@liquidweb-dev/undercurrent/classes/pseudo';
import Link from '@liquidweb-dev/undercurrent/Link';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import { Link as RouterLink } from 'react-router-dom';
import {
	CreatedDate,
	LastReplyChip,
} from 'components/standard/SupportCases/Cases';
import StatusDot, { type StatusDotVariant } from 'components/icons/StatusDot';
import CancellationModalHeader from './CancellationModalHeader';
import CancellationLoader from './CancellationLoader';
import { TICKET_STATUS, type CancellationFormData } from './constants';

const getStatusConfig = (
	status?: string,
): { title: string; variant: StatusDotVariant } => {
	switch (status) {
		case TICKET_STATUS.WAITING_ON_CUSTOMER:
			return { title: 'Needs attention', variant: 'warning' };
		case TICKET_STATUS.NEW:
		case TICKET_STATUS.OPEN:
			return { title: status, variant: 'success' };
		default:
			return { title: 'Closed', variant: 'neutral' };
	}
};

const getLoaderText = (
	ticketId: string | undefined,
	isTicketCreationSuccess: boolean,
) => {
	if (isTicketCreationSuccess) {
		return 'Finishing up...';
	}
	if (!ticketId) {
		return "We're creating your cancellation case...";
	}
	return 'Fetching details...';
};

interface TicketDetailsProps {
	ticketId: string | undefined;
	status: string | undefined;
	subject: string | undefined;
	createdAt: string | undefined;
	lastReply: string | null | undefined;
}

const TicketDetails = ({
	ticketId,
	status,
	subject,
	createdAt,
	lastReply,
}: TicketDetailsProps) => {
	const { title, variant } = getStatusConfig(status);
	const domain = subject?.substring(25);

	return (
		<Card sx={{ width: '100%' }}>
			<CardContent
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					justifyContent: 'space-between',
					gap: 100,
					[pseudoClasses.important]: {
						padding: 300,
					},
				}}
			>
				<Stack
					display="flex"
					alignItems="center"
					gap={{ xs: 0, md: 100 }}
					flexDirection={{ xs: 'column', md: 'row' }}
				>
					<Stack
						display="flex"
						flexDirection="row"
						alignItems="center"
						gap={200}
					>
						{status && (
							<Tooltip title={title}>
								<IconButton variant="barebones" size="large">
									<StatusDot variant={variant} />
								</IconButton>
							</Tooltip>
						)}
						<Typography variant="body-md" sx={{ textWrap: 'nowrap' }}>
							Cancellation Assistance:
						</Typography>
					</Stack>
					<Stack
						display="flex"
						flexDirection="row"
						alignItems="center"
						justifyContent="center"
						gap={100}
						sx={{ width: { xs: '100%', md: '230px' } }}
					>
						<Tooltip title={domain}>
							<TypographyOverflow variant="body-md" alignItems="center">
								{domain}
							</TypographyOverflow>
						</Tooltip>
					</Stack>
					<Stack
						display="flex"
						flexDirection={{ xs: 'column', md: 'row' }}
						alignItems="center"
						gap={100}
					>
						<Typography
							variant="body-md"
							maxWidth="152px"
							sx={(theme) => ({ color: theme.vars.palette.uc.text.weak })}
						>
							<Link
								component={RouterLink}
								to={`/support/mycases/${ticketId}`}
								sx={{ textDecoration: 'none' }}
							>
								{ticketId}
							</Link>
						</Typography>
					</Stack>
				</Stack>
				<Stack
					display="flex"
					alignItems="center"
					gap={100}
					flexDirection={{ xs: 'column', md: 'row' }}
					alignSelf="stretch"
				>
					{lastReply && <LastReplyChip lastReply={lastReply} />}
					<Stack
						display="flex"
						justifyContent="center"
						alignItems="center"
						sx={(theme) => ({ height: theme.size.sm })}
					>
						{createdAt && <CreatedDate createdDate={createdAt} />}
					</Stack>
				</Stack>
			</CardContent>
		</Card>
	);
};

export interface SubmittedRequestProps {
	uniqId: string;
	ticketId: string | undefined;
	formValues: CancellationFormData;
	onCancelWithoutAssistance: () => void;
}

const SubmittedRequest = ({
	uniqId,
	ticketId,
	formValues,
	onCancelWithoutAssistance,
}: SubmittedRequestProps) => {
	const { data: ticketDetails, isLoading: isTicketDetailsLoading } =
		useSupportRequestDetails(
			{ ticket_number: ticketId },
			{ enabled: !!ticketId },
		);

	const {
		mutate: createCancellationTicket,
		isIdle: isTicketCreationIdle,
		isLoading: isTicketCreationOnProgress,
		isSuccess: isTicketCreationSuccess,
	} = useAssetCancellationRequestCreate();

	const {
		status,
		subject,
		created_date: ticketCreatedAt,
		last_outbound_email: lastReply,
	} = ticketDetails || {};

	const loadStatus = getLoaderText(ticketId, isTicketCreationSuccess);

	const loadIcon = isTicketCreationSuccess ? (
		<CircleCheckIcon
			sx={(theme) => ({
				width: theme.size.xS,
				height: theme.size.xS,
				color: theme.vars.palette.uc.success.icon.main,
			})}
		/>
	) : (
		<LoaderCircleIcon
			sx={(theme) => ({
				width: theme.size.xS,
				height: theme.size.xS,
			})}
		/>
	);

	useEffect(() => {
		if (!ticketId && isTicketCreationIdle) {
			createCancellationTicket({
				uniq_id: uniqId,
				cancellation_reason: formValues.cancellation_reason,
				additional_information: formValues.additional_info,
			});
		}
	}, [
		ticketId,
		createCancellationTicket,
		uniqId,
		formValues,
		isTicketCreationIdle,
	]);

	return (
		<Stack
			flexDirection="column"
			alignItems="center"
			gap={300}
			alignSelf="stretch"
		>
			<CancellationModalHeader>
				<Stack
					display="flex"
					flexDirection="row"
					width="100%"
					justifyContent="center"
				>
					<Typography
						variant="title-md"
						fontWeight={500}
						sx={(theme) => ({ color: theme.vars.palette.uc.text.main })}
					>
						We&apos;ve received your cancellation request
					</Typography>
				</Stack>
				<Typography variant="body-md" textAlign="center">
					We&apos;ve received your request and a specialist will reach out
					within 24-48 hours to help finalize the termination of your service.
					You can view or manage your request at any time using the case below.
				</Typography>
			</CancellationModalHeader>
			<Stack
				display="flex"
				flexDirection="column"
				alignItems="flex-start"
				gap={200}
				alignSelf="stretch"
			>
				<Typography variant="label-lg" fontWeight={500}>
					Your cancellation case
				</Typography>
				{isTicketCreationOnProgress || isTicketDetailsLoading ? (
					<CancellationLoader icon={loadIcon} title={loadStatus} width="100%" />
				) : (
					<TicketDetails
						ticketId={ticketId}
						status={status}
						subject={subject}
						createdAt={ticketCreatedAt}
						lastReply={lastReply}
					/>
				)}
			</Stack>
			<Stack display="flex" alignItems="center" alignSelf="stretch">
				<Button
					size="large"
					variant="link"
					sx={(theme) => ({
						height: theme.size.md,
						border: `1px solid ${theme.vars.palette.uc.bg.main}`,
						padding: `${theme.spacing(100)} ${theme.spacing(150)}`,
						color: theme.vars.palette.uc.text.weak,
					})}
					onClick={onCancelWithoutAssistance}
				>
					Prefer to handle it on your own? Cancel without assistance
				</Button>
			</Stack>
		</Stack>
	);
};

export default SubmittedRequest;
